import { useContext, useState, useEffect, useRef } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import ReactJson from 'react-json-view';
import { Button } from '@playcastdotio/Shadcn';
import constants from '@playcastdotio/GlobalConfig';
import { PresetControls } from './PresetControls';
import { AfkPreventionControls } from './AfkPreventionControls';
import { ShadowDisplayControl } from './ShadowDisplayControl';
import { BitrateControl } from './BitrateControl';
import { CameraResolutionControl } from './CameraResolutionControl';

export const ReleaseDevData = () => {
  const context = useContext(PlaycastContext);
  const { stateStores } = context;

  const handleWebrtcInternalsClick = () => {
    context.dev?.showWebrtcInternals();
  };

  const handleGpuInternalsClick = () => {
    context.dev?.showGpuInternals();
  };

  // const handleToggleVideoPreview = () => {
  //   context.targetFunctions.showVideoPreview();
  // };

  // const PREVIEW_CAMERAS = [
  //   'ShadowCam00',
  //   'ShadowCam01',
  //   'ShadowCam02',
  //   'ShadowCam03',
  //   'ShadowCam04',
  //   'ShadowCam05',
  //   'ShadowCam06',
  //   'ShadowCam07',
  //   'ShadowCam08',
  // ];

  // const handleSelectPreviewCamera = (camera: string) => {
  //   context.electronFunctions?.switchVideoPreviewCamera(camera);
  // };

  const handleCopyStateStores = () => {
    const sanitizedStates: Partial<typeof stateStores> = {
      ...stateStores,
    };

    // Remove sensitive states
    delete sanitizedStates.authState;
    delete sanitizedStates.loginProfileState;
    delete sanitizedStates.apiState;
    delete sanitizedStates.invitationSendState;
    delete sanitizedStates.qrCodeState;
    delete sanitizedStates.hostQrCodeState;
    delete sanitizedStates.remoteHostList;
    delete sanitizedStates.knockMetadataState;
    delete sanitizedStates.preferenceState;

    sanitizedStates.staticImageState = {
      players: `${sanitizedStates.staticImageState?.players.length ?? 0} players` as any,
      staticImageBase64: `${sanitizedStates.staticImageState?.staticImageBase64?.length ?? 0} bytes` as any,
    };

    const debugDump = `
---

<details>
<summary>
<h3>Debug Dump: Host</h3>
</summary>

\`\`\`json
${JSON.stringify(sanitizedStates, null, 2)}
\`\`\`
</details>

---
`;
    navigator.clipboard.writeText(debugDump);
  };

  const [shouldShow, setShouldShow] = useState(false);
  const savedBoundsRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Move keydown event listener into an effect with cleanup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F12' && e.ctrlKey) {
        if (constants.ENVIRONMENT === 'production') {
          setShouldShow((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove the event listener when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    const wc = window.windowControls;
    if (!wc) return;

    if (shouldShow) {
      void (async () => {
        try {
          const cur = await wc.getBounds();
          savedBoundsRef.current = cur;
          const DEV_W = 1200;
          const DEV_H = 800;
          const nx = Math.round((window.screen.availWidth - DEV_W) / 2);
          const ny = Math.round((window.screen.availHeight - DEV_H) / 2);
          await wc.setBounds({ x: nx, y: ny, width: DEV_W, height: DEV_H });
        } catch (e) {
          console.warn('[ReleaseDevData] window resize failed', e);
        }
      })();
    } else if (savedBoundsRef.current) {
      const bounds = savedBoundsRef.current;
      savedBoundsRef.current = null;
      void (async () => {
        try {
          await wc.resetPavilionBounds?.(bounds);
        } catch (e) {
          console.warn('[ReleaseDevData] window restore failed', e);
        }
      })();
    }
  }, [shouldShow]);

  const sanitizedDisplayStates: Partial<typeof stateStores> = {
    ...stateStores,
  };

  // Remove sensitive states
  delete sanitizedDisplayStates.authState;
  delete sanitizedDisplayStates.loginProfileState;
  delete sanitizedDisplayStates.apiState;
  delete sanitizedDisplayStates.invitationSendState;
  delete sanitizedDisplayStates.qrCodeState;
  delete sanitizedDisplayStates.hostQrCodeState;
  delete sanitizedDisplayStates.remoteHostList;
  delete sanitizedDisplayStates.knockMetadataState;
  delete sanitizedDisplayStates.preferenceState;

  return (
    shouldShow && (
      <div className='fixed inset-0 z-[9999] overflow-auto bg-black/90 p-4 flex flex-col space-y-6 text-white'>
        {/* Debug Tools Section */}
        <div>
          <div className='my-2 flex flex-row space-x-1'>
            <Button className='m-0' onClick={handleWebrtcInternalsClick}>
              Open WebRTC Internals
            </Button>
            <Button className='m-0' onClick={handleGpuInternalsClick}>
              GPU Stats
            </Button>
          </div>

          {/* Video Preview Controls */}
          {/* <div className='my-2 flex flex-row space-x-1'>
            <Button className='m-0' onClick={handleToggleVideoPreview}>
              Toggle Preview
            </Button>
            <Select value={stateStores.hostRuleState.videoPreviewCamera} onValueChange={handleSelectPreviewCamera}>
              <SelectTrigger>
                <SelectValue placeholder='Select a preview camera' />
              </SelectTrigger>
              <SelectContent>
                {PREVIEW_CAMERAS.map((camera) => (
                  <SelectItem key={camera} value={camera}>
                    {camera}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}
        </div>

        {/* Preset Controls */}
        <PresetControls />

        {/* Bitrate Override */}
        <BitrateControl />

        {/* AFK Prevention Controls */}
        <AfkPreventionControls />

        {/* ShadowDisplay Driver Testing */}
        <ShadowDisplayControl />

        {/* ShadowCam03 Capture Resolution */}
        <CameraResolutionControl />

        {/* Debug Data Section */}
        <div className='flex flex-row space-x-1'>
          <Button className='m-0' onClick={handleCopyStateStores}>
            Copy Diagnostic Data
          </Button>
        </div>

        {/* State Viewer with Sanitized Data */}
        <ReactJson
          name={false}
          src={sanitizedDisplayStates}
          theme={'harmonic'}
          collapseStringsAfterLength={80}
          collapsed={1}
          indentWidth={2}
          style={{ fontSize: '16px' }}
        />
      </div>
    )
  );
};
